<script setup lang="ts">
/**
 * The admin surface's locked takeover. `02.capability-route-protection` records the
 * refused route and the default layout renders this inside the admin shell in place of
 * the page component, so the sidebar stays usable, a deep link behaves exactly like a
 * nav click, and no page data is ever fetched.
 */
import { Illustration } from '~ds/components/ui/illustration-system';
import { Heading, Text } from '~ds/components/ui/typography';
import padlockUrl from '~kui/public/images/kui/padlock.webp';

import type { Capability } from '~/graphql/generated/graphql';

interface Props {
  missing: Capability[];
}

const props = defineProps<Props>();

const { t } = useI18n();

const lockedRoute = useContentLockedRoute();
const authStore = useAuthStore();
const { canAll } = useCapabilities();

// The route gate reads the session's cached viewer, so a capability granted after login stays
// invisible until a full reload — which is exactly the moment the grant UI creates. Revalidating
// the viewer as the takeover mounts converges the grant on the next tick, since the layout gates
// on this state. Once per takeover: a refusal that still stands simply leaves the lock in place.
onMounted(async () => {
  // Opportunistic — a failed revalidation leaves the lock rather than replacing it with an error.
  await authStore.fetchUser(true).catch(() => undefined);

  if (props.missing.length && canAll(props.missing)) {
    lockedRoute.value = null;
  }
});
</script>

<template>
  <!-- Centred in the admin shell's content pane, so the height discounts the shell's
       sticky header (3.5rem) and the page container's own vertical padding (3rem). -->
  <div
    data-testid="content-locked-state"
    class="flex min-h-[calc(100dvh-6.5rem)] items-center justify-center px-4"
  >
    <div class="flex max-w-md flex-col items-center justify-center gap-4 text-center">
      <Illustration
        :src="padlockUrl"
        alt=""
        size="auto"
        class="w-24"
      />
      <Heading
        size="2xl"
        as="h1"
      >
        {{ t('content.locked.title') }}
      </Heading>
      <Text
        v-if="missing.length"
        data-testid="content-locked-capabilities"
      >
        {{ t('content.locked.capability', { capability: missing.join(', ') }) }}
      </Text>
      <Text>{{ t('content.locked.requestAccess') }}</Text>
    </div>
  </div>
</template>
